/**
 * Inventory quantity picker for RMA processing — covers remove-from-inventory,
 * add-to-inventory, and packing supplies.
 *
 * Loads warehouse products (filtered by a `productFilter` predicate) and
 * feeds them to the generic PartQuantityGrid. The parent owns the selected
 * quantities as an Immutable Map keyed by product id string.
 *
 * Contains:
 * - InventoryQtySelect — warehouse-product-backed wrapper around PartQuantityGrid
 */

import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import PartQuantityGrid from '@sophocles/components/PartQuantityGrid';
import { loadProducts } from '@sophocles/features/warehouse/products/api';
import { productSlice } from '@sophocles/features/warehouse/products/slice';

const QTY_CHOICES_SMALL = [0, 1, 2, 3, 4, 5];

/**
 * @param {object} props
 * @param {string} props.title              - Section heading
 * @param {object} props.fetchParams        - Params forwarded to loadProducts (e.g. { used_in_rma: true })
 * @param {function} props.productFilter    - (product) => boolean, filters from slice
 * @param {import('immutable').Map} props.value    - Map of { "productId": qty }
 * @param {function} props.onQtyChange      - (productId, qty) => void
 * @param {number[]} [props.qtyChoices]     - Defaults to QTY_CHOICES_SMALL
 * @param {boolean} [props.defaultOpen]
 */
export default function InventoryQtySelect({
  title,
  fetchParams,
  productFilter,
  value,
  onQtyChange,
  qtyChoices = QTY_CHOICES_SMALL,
  defaultOpen = false,
}) {
  const dispatch = useDispatch();
  const products = useSelector(state => productSlice.selectors.selectAllEntities(state));
  const isLoading = useSelector(state => productSlice.selectors.selectIsLoading(state));

  useEffect(() => {
    if (defaultOpen) {
      dispatch(loadProducts({ ...fetchParams, limit: 1000 }));
    }
  }, []);

  const items = [...products]
    .filter(productFilter)
    .sort((a, b) => (a.item_part_num ?? '').localeCompare(b.item_part_num ?? ''))
    .map(product => ({
      id: product.id,
      partNumber: product.item_part_num,
      name: product.item_name,
      description: product.item_description,
    }));

  return (
    <PartQuantityGrid
      title={title}
      items={items}
      getQty={id => value.get(id + '', 0)}
      onQtyChange={onQtyChange}
      qtyChoices={qtyChoices}
      defaultOpen={defaultOpen}
      loading={isLoading}
      onExpand={() => dispatch(loadProducts({ ...fetchParams, limit: 1000 }))}
    />
  );
}
